import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  FaSave, FaTrash, FaPlus, FaArrowUp, FaArrowDown, FaEdit, FaUpload,
  FaDownload, FaEye, FaEyeSlash, FaHome, FaChevronDown, FaChevronRight, FaCheck
} from 'react-icons/fa';
import {
  Drill, Droplets, Wind, Wrench, Cog, Activity, LineChart, TestTube,
  Anchor, Truck, Settings, FileSearch, HardHat, Box, Layers, Factory, Cpu
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

// Available Lucide icons
const LUCIDE_ICONS = [
  { name: 'Drill', icon: Drill, label: 'Foreuse' },
  { name: 'Droplets', icon: Droplets, label: 'Pompe/Liquide' },
  { name: 'Wind', icon: Wind, label: 'Compresseur' },
  { name: 'Wrench', icon: Wrench, label: 'Outil' },
  { name: 'Cog', icon: Cog, label: 'Pièce/Engrenage' },
  { name: 'Activity', icon: Activity, label: 'Instrumentation' },
  { name: 'LineChart', icon: LineChart, label: 'Monitoring' },
  { name: 'TestTube', icon: TestTube, label: 'Essais' },
  { name: 'Anchor', icon: Anchor, label: 'Fondations' },
  { name: 'Truck', icon: Truck, label: 'Transport' },
  { name: 'Settings', icon: Settings, label: 'Services' },
  { name: 'FileSearch', icon: FileSearch, label: 'Études' },
  { name: 'HardHat', icon: HardHat, label: 'Talents' },
  { name: 'Box', icon: Box, label: 'Général' },
  { name: 'Layers', icon: Layers, label: 'Couches' },
  { name: 'Factory', icon: Factory, label: 'Industrie' },
  { name: 'Cpu', icon: Cpu, label: 'Logiciel' },
];

// GEOTS color palette
const COLOR_OPTIONS = [
  { hex: '#635BFF', name: 'Violet' },
  { hex: '#00D4FF', name: 'Cyan' },
  { hex: '#3B82F6', name: 'Bleu' },
  { hex: '#2563EB', name: 'Bleu foncé' },
  { hex: '#1D4ED8', name: 'Bleu très foncé' },
  { hex: '#06B6D4', name: 'Cyan foncé' },
  { hex: '#14B8A6', name: 'Turquoise' },
  { hex: '#10B981', name: 'Vert' },
  { hex: '#F97316', name: 'Orange' },
  { hex: '#FB923C', name: 'Orange clair' },
  { hex: '#FCD34D', name: 'Jaune' },
  { hex: '#A855F7', name: 'Violet clair' },
  { hex: '#94A3B8', name: 'Gris ardoise' },
];

const getIconComponent = (iconName, size = 20) => {
  const found = LUCIDE_ICONS.find(i => i.name === iconName);
  if (found) {
    const IconComp = found.icon;
    return <IconComp size={size} />;
  }
  return <Box size={size} />;
};

export default function AdminCategories() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [groupedCategories, setGroupedCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [stats, setStats] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    slug: '',
    description_fr: '',
    description_en: '',
    icon_lucide: 'Box',
    color: '#635BFF',
    group_id: '',
    position: 999,
    active: true,
    visible_home: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupedRes, groupsRes, statsRes] = await Promise.all([
        axios.get(`${API}/categories/with-groups?active_only=false&include_stats=true`),
        axios.get(`${API}/categories/groups`),
        axios.get(`${API}/categories/stats/overview`),
      ]);
      
      setGroupedCategories(groupedRes.data);
      setGroups(groupsRes.data);
      setStats(statsRes.data);
      
      // Expand all groups by default
      const expanded = {};
      groupedRes.data.forEach(g => { expanded[g.group.id] = true; });
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/categories`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie créée');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name_fr: '',
      name_en: '',
      slug: '',
      description_fr: '',
      description_en: '',
      icon_lucide: 'Box',
      color: '#635BFF',
      group_id: groups[0]?.id || '',
      position: 999,
      active: true,
      visible_home: true,
    });
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name_fr: category.name_fr || '',
      name_en: category.name_en || '',
      slug: category.slug || '',
      description_fr: category.description_fr || '',
      description_en: category.description_en || '',
      icon_lucide: category.icon_lucide || 'Box',
      color: category.color || '#635BFF',
      group_id: category.group_id || '',
      position: category.position || 999,
      active: category.active !== false,
      visible_home: category.visible_home !== false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId, productCount) => {
    if (productCount > 0) {
      const confirmed = window.confirm(
        `Cette catégorie contient ${productCount} produits.\n\nVoulez-vous vraiment la supprimer ? Les produits seront marqués "à classer".`
      );
      if (!confirmed) return;
    } else {
      if (!window.confirm('Supprimer cette catégorie ?')) return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/categories/${categoryId}?mark_unclassified=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/categories/${categoryId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.active ? 'Catégorie activée' : 'Catégorie désactivée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleVisibleHome = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/categories/${categoryId}/toggle-visible-home`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.visible_home ? 'Visible sur la home' : 'Masquée de la home');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/categories/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'categories_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Import en cours...');
      const response = await axios.post(`${API}/categories/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      toast.success(`Import terminé : ${response.data.imported} créées, ${response.data.updated} mises à jour`);
      fetchData();
    } catch (error) {
      toast.dismiss();
      toast.error('Erreur lors de l\'import');
      console.error('Import error:', error);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-categories">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#0A2540]">Gestion des Catégories</h1>
              <p className="text-gray-600 text-sm mt-1">
                {stats && `${stats.total_categories} catégories • ${stats.categories_with_products} avec produits`}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportCSV}
                accept=".csv"
                className="hidden"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <FaUpload className="mr-2" /> Import CSV
              </Button>
              <Button variant="outline" onClick={handleExportCSV}>
                <FaDownload className="mr-2" /> Export CSV
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#635BFF] hover:bg-[#635BFF]/90" onClick={openNewDialog}>
                    <FaPlus className="mr-2" /> Nouvelle catégorie
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Names */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom (FR) *</Label>
                        <Input 
                          required 
                          value={formData.name_fr} 
                          onChange={(e) => setFormData({...formData, name_fr: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Name (EN) *</Label>
                        <Input 
                          required 
                          value={formData.name_en} 
                          onChange={(e) => setFormData({...formData, name_en: e.target.value})} 
                        />
                      </div>
                    </div>

                    {/* Slug & Group */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Slug (URL)</Label>
                        <Input 
                          value={formData.slug} 
                          onChange={(e) => setFormData({...formData, slug: e.target.value})}
                          placeholder="auto-généré si vide"
                        />
                      </div>
                      <div>
                        <Label>Groupe</Label>
                        <select
                          value={formData.group_id}
                          onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                          className="w-full h-10 border border-gray-200 rounded-md px-3"
                        >
                          <option value="">-- Sans groupe --</option>
                          {groups.map(g => (
                            <option key={g.id} value={g.id}>{g.name_fr}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Descriptions */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Description (FR)</Label>
                        <Input 
                          value={formData.description_fr} 
                          onChange={(e) => setFormData({...formData, description_fr: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Description (EN)</Label>
                        <Input 
                          value={formData.description_en} 
                          onChange={(e) => setFormData({...formData, description_en: e.target.value})} 
                        />
                      </div>
                    </div>

                    {/* Icon Selection */}
                    <div>
                      <Label>Icône Lucide</Label>
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg mt-1 max-h-32 overflow-y-auto">
                        {LUCIDE_ICONS.map(({ name, icon: Icon, label }) => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => setFormData({...formData, icon_lucide: name})}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors ${
                              formData.icon_lucide === name ? 'bg-[#635BFF]/20 ring-2 ring-[#635BFF]' : ''
                            }`}
                            title={label}
                          >
                            <Icon size={20} style={{ color: formData.color }} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color Selection */}
                    <div>
                      <Label>Couleur</Label>
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg mt-1">
                        {COLOR_OPTIONS.map(({ hex, name }) => (
                          <button
                            key={hex}
                            type="button"
                            onClick={() => setFormData({...formData, color: hex})}
                            className={`w-8 h-8 rounded-full transition-transform hover:scale-110 flex items-center justify-center ${
                              formData.color === hex ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                            }`}
                            style={{ backgroundColor: hex }}
                            title={name}
                          >
                            {formData.color === hex && <FaCheck className="text-white text-xs" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Toggles */}
                    <div className="flex flex-wrap gap-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.active}
                          onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                        />
                        <Label className="cursor-pointer">Active</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.visible_home}
                          onCheckedChange={(checked) => setFormData({...formData, visible_home: checked})}
                        />
                        <Label className="cursor-pointer">Visible sur la home</Label>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <Label className="text-xs text-gray-500 mb-2 block">Aperçu</Label>
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${formData.color}20` }}
                        >
                          <span style={{ color: formData.color }}>
                            {getIconComponent(formData.icon_lucide, 28)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-[#0A2540]">{formData.name_fr || 'Nom de la catégorie'}</div>
                          <div className="text-sm text-gray-500">{formData.description_fr || 'Description...'}</div>
                        </div>
                        <div 
                          className="px-3 py-1.5 rounded-full text-white text-sm font-medium"
                          style={{ backgroundColor: formData.color }}
                        >
                          {getIconComponent(formData.icon_lucide, 14)}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="bg-[#635BFF]" disabled={saving}>
                        <FaSave className="mr-2" />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Categories by Group */}
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedCategories.map(({ group, categories }) => (
                <Card key={group.id}>
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedGroups[group.id] ? (
                        <FaChevronDown className="text-gray-400" />
                      ) : (
                        <FaChevronRight className="text-gray-400" />
                      )}
                      <h2 className="font-bold text-[#0A2540]">{group.name_fr}</h2>
                      <span className="text-sm text-gray-500">({categories.length} catégories)</span>
                    </div>
                  </button>

                  {/* Categories List */}
                  {expandedGroups[group.id] && (
                    <CardContent className="p-0 border-t">
                      <div className="divide-y divide-gray-100">
                        {categories.map((category) => (
                          <div
                            key={category.id}
                            className={`p-4 flex items-center gap-4 ${!category.active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'} transition-colors`}
                          >
                            {/* Icon */}
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${category.color || '#635BFF'}20` }}
                            >
                              <span style={{ color: category.color || '#635BFF' }}>
                                {getIconComponent(category.icon_lucide, 24)}
                              </span>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-[#0A2540]">{category.name_fr}</h3>
                                {!category.active && (
                                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Inactive</span>
                                )}
                                {!category.visible_home && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Masquée home</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 truncate">{category.description_fr}</p>
                            </div>

                            {/* Product Count */}
                            <div 
                              className="px-3 py-1.5 rounded-full text-sm font-medium"
                              style={{ 
                                backgroundColor: `${category.color || '#635BFF'}15`,
                                color: category.color || '#635BFF'
                              }}
                            >
                              {category.product_count || 0} produits
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleActive(category.id)}
                                title={category.active ? 'Désactiver' : 'Activer'}
                                className="h-8 w-8 p-0"
                              >
                                {category.active ? <FaEye className="text-green-500" /> : <FaEyeSlash className="text-gray-400" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleVisibleHome(category.id)}
                                title={category.visible_home ? 'Masquer de la home' : 'Afficher sur la home'}
                                className="h-8 w-8 p-0"
                              >
                                <FaHome className={category.visible_home ? 'text-blue-500' : 'text-gray-400'} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(category)}
                                className="h-8"
                              >
                                <FaEdit className="w-3 h-3 mr-1" /> Modifier
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(category.id, category.product_count || 0)}
                              >
                                <FaTrash className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {categories.length === 0 && (
                          <div className="p-8 text-center text-gray-500">
                            Aucune catégorie dans ce groupe
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}

          {/* Help */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-blue-800 mb-2">💡 Conseils</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Cliquez sur 👁️ pour activer/désactiver une catégorie (invisible publiquement)</li>
              <li>• Cliquez sur 🏠 pour masquer/afficher une catégorie sur la page d'accueil</li>
              <li>• Les catégories inactives sont grisées et non visibles sur le site public</li>
              <li>• Exportez en CSV pour sauvegarder ou modifier en masse</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
